'use client';

import { ApiClientContext } from './ApiClientContext';
import { PathName } from '@/modules/PathName';
import { useSafeNavigator } from '@/modules/SafeNavigator';
import {
  getCookie,
  removeCookies,
  removeWorkspaceTokenCookies,
  setCookie,
} from '@/utils/cookie/BrowserCookie';
import { setUserToSentry } from '@/utils/sentry';
import {
  AgreementBase,
  // AppleAuthentication,
  Body_signup_v1_user_post,
  LoginResponse,
  PushDeviceRequest,
  User,
  UserStatus,
  UserUpdateRequest,
} from 'callabo-api/src';
import { CallaboApiClient } from 'callabo-api/src/CallaboApiClient';
import { ApiError } from 'callabo-api/src/core/ApiError';
import {
  GoogleAuthProvider,
  // OAuthProvider,
  getAuth,
  signInWithPopup,
} from 'firebase/auth';
import { UserModel } from 'libs/callabo-state/models/UserModel';
import { Phase } from 'libs/callabo-state/types/Phase';
import { dateIsOver } from 'libs/rtzr-commons/DateUtils';
import { getOrCreateDeviceId } from 'libs/rtzr-commons/FingerprintUtils';
import getIndexedDbStorageAsync from 'libs/rtzr-commons/IndexedDbStorage';
import analyticsInstance from 'libs/rtzr-commons/modules/marketing/Analytics';
import useEventCallback from 'libs/rtzr-commons/useEventCallback';
import React, { createContext, useCallback, useEffect } from 'react';
import { AuthTokenManager } from 'src/api/AuthTokenManager';
import { CALLABO_API_BASE_URL, Paths } from 'src/modules/Paths';
import {
  loginWithEmailAction,
  loginWithOAuthAction,
  loginWithPasswordSetup,
  logoutAction,
} from '../../app/[locale]/login/actions';
import { isActionError } from 'app/util/types';
import { modifyUserAction } from './actions';
import { useStableState } from '@/hooks/useStableState';
import { LoginFormValue } from 'app/[locale]/login/components/LoginWithEmailForm';
import { getMsalInstance } from '@/utils/MSAuthUtil';
import { AppStorage } from '@/modules/AppStorage';
import { usePushToken } from 'app/components/FirebaseStore';
import { resetTooltip } from '@/hooks/useTooltip';
import { ACCESS_TOKEN_EXPIRED_BUFFER } from 'app/util/const';
import { useTrustedDevice } from '@/hooks/useTrustedDevice';

export class AuthResponseError extends Error {
  constructor(message?: string) {
    super(message);

    // Set the prototype explicitly.
    // https://www.typescriptlang.org/docs/handbook/2/classes.html#inheriting-built-in-types
    Object.setPrototypeOf(this, AuthResponseError.prototype);
  }
}

export class InvalidInvitationTokenError extends AuthResponseError {
  constructor(message?: string) {
    super(message);

    // Set the prototype explicitly.
    // https://www.typescriptlang.org/docs/handbook/2/classes.html#inheriting-built-in-types
    Object.setPrototypeOf(this, InvalidInvitationTokenError.prototype);
  }
}
export class UnownedInvitationTokenError extends AuthResponseError {
  constructor(message?: string) {
    super(message);

    // Set the prototype explicitly.
    // https://www.typescriptlang.org/docs/handbook/2/classes.html#inheriting-built-in-types
    Object.setPrototypeOf(this, UnownedInvitationTokenError.prototype);
  }
}

export class DuplicatedEmailError extends AuthResponseError {
  constructor(message?: string) {
    super(message);

    // Set the prototype explicitly.
    // https://www.typescriptlang.org/docs/handbook/2/classes.html#inheriting-built-in-types
    Object.setPrototypeOf(this, DuplicatedEmailError.prototype);
  }
}

export class RateLimitedError extends AuthResponseError {
  constructor(message?: string) {
    super(message);

    // Set the prototype explicitly.
    // https://www.typescriptlang.org/docs/handbook/2/classes.html#inheriting-built-in-types
    Object.setPrototypeOf(this, RateLimitedError.prototype);
  }
}

export class InvalidCredentialsError extends AuthResponseError {
  constructor(message?: string) {
    super(message);

    // Set the prototype explicitly.
    // https://www.typescriptlang.org/docs/handbook/2/classes.html#inheriting-built-in-types
    Object.setPrototypeOf(this, InvalidCredentialsError.prototype);
  }
}

const FIREBASE_ERROR_CODE_EXCEPTIONS = [
  'auth/popup-closed-by-user',
  'auth/user-cancelled',
  'auth/cancelled-popup-request',
  'auth/popup-blocked',
  'installations/app-offline',
];

const MSAL_ERROR_CODE_EXCEPTIONS = [
  'user_cancelled',
  'access_denied',
  'interaction_in_progress',
  'popup_window_error',
];

export type AuthState =
  | {
      type: 'loading';
    }
  | {
      type: 'signedIn';
      callaboApiClient: CallaboApiClient;
      token: {
        accessToken: string;
        refreshToken: string;
        expiredDate: Date;
      };
      user: UserModel;
    }
  | {
      type: 'signedOut';
      callaboApiClient: CallaboApiClient;
    };

interface IState {
  authState: AuthState;
  signUpFinishAsync: (
    greements: Array<AgreementBase>,
    name: string,
    company: string,
    token?: string
  ) => Promise<void>;
  signUpEmailAsync: (
    data: Body_signup_v1_user_post,
    token: { verify?: string; invitation?: string }
  ) => Promise<void>;
  loginGoogleAsync: (token?: string, loginHint?: string) => Promise<void>;
  loginMicrosoftAsync: (token?: string, loginHint?: string) => Promise<void>;
  // loginAppleAsync: (token?: string, loginHint?: string) => Promise<void>;
  loginEmailAsync: (data: LoginFormValue) => Promise<void>;
  loginPasswordSetupAsync: (password: string, token: string) => Promise<void>;
  modifyUserAsync: (data: UserUpdateRequest) => Promise<void>;
  logoutAsync: (redirect?: string) => Promise<void>;
  withdrawAsync: () => Promise<void>;
  getAuthState: () => AuthState;
}

interface IProps {
  children?: React.ReactNode;
  me: User | undefined;
}

export const AuthContext = createContext<IState>({
  authState: { type: 'loading' },
  signUpFinishAsync: () => undefined,
  signUpEmailAsync: () => undefined,
  loginGoogleAsync: () => undefined,
  loginMicrosoftAsync: () => undefined,
  // loginAppleAsync: () => undefined,
  loginEmailAsync: () => undefined,
  loginPasswordSetupAsync: () => undefined,
  modifyUserAsync: () => undefined,
  logoutAsync: () => undefined,
  withdrawAsync: () => undefined,
  getAuthState: () => ({ type: 'loading' }),
});

const PHASE = process.env.NEXT_PUBLIC_PHASE;

/**
 * auth에 대해서만 관리
 * @param props
 * @returns
 */
export const AuthProvider: React.FC<IProps> = (props: IProps) => {
  const [authState, setAuthState, getAuthState] = useStableState<AuthState>({ type: 'loading' });
  const pushToken = usePushToken();
  const navigator = useSafeNavigator();
  const { getTrustedTokenByCurrentEmail } = useTrustedDevice();

  const onSignIn = async (loginResponse: LoginResponse) => {
    const accessToken = loginResponse.token.access_token;
    const refreshToken = loginResponse.token.refresh_token;
    const tokenCreateAt = loginResponse.token.created_at;
    const tokenExpiredDate = new Date(tokenCreateAt);
    const expires = Math.round(loginResponse.token.expires_in * ACCESS_TOKEN_EXPIRED_BUFFER);
    tokenExpiredDate.setSeconds(tokenExpiredDate.getSeconds() + expires);

    const authTokenManager = await AuthTokenManager.createAsync(
      getOrCreateDeviceId(),
      async (response) => {
        try {
          await onSignIn(response);
        } catch (e) {
          onSignOut();
        }
      }
    );
    authTokenManager.event.on('unauthorized', onSignOut);

    const storage = await getIndexedDbStorageAsync();
    await storage.setMany([
      {
        key: 'access_token',
        value: accessToken,
      },
      {
        key: 'refresh_token',
        value: refreshToken,
      },
      {
        key: 'token_expired_date',
        value: tokenExpiredDate.toISOString(),
      },
    ]);

    setAuthState({
      type: 'signedIn',
      callaboApiClient: new CallaboApiClient({
        BASE: CALLABO_API_BASE_URL,
        TOKEN: accessToken,
        INSTANCE: authTokenManager.axiosInstance,
      }),
      user: new UserModel(loginResponse.user),
      token: {
        accessToken,
        refreshToken,
        expiredDate: tokenExpiredDate,
      },
    });

    // user locale을 브라우저에 업데이트
    setCookie('locale', loginResponse.user.locale, 30 * 24 * 60 * 60);
    setCookie('access_token', accessToken, expires);
    setCookie('refresh_token', refreshToken, 30 * 24 * 60 * 60);
    setCookie('token_expired_date', tokenExpiredDate.toISOString(), expires);
  };

  const onSignOut = useEventCallback(async (redirectUrl?: string) => {
    removeCookies([
      'locale',
      'access_token',
      'refresh_token',
      'token_expired_date',
      'last_used_workspace_id',
      'upload_options',
      'fetch_options',
    ]);
    removeWorkspaceTokenCookies();
    setAuthState({
      type: 'signedOut',
      callaboApiClient: new CallaboApiClient({ BASE: CALLABO_API_BASE_URL }),
    });
    navigator.push(redirectUrl ?? new PathName(Paths.Login).toString());
  });

  const setTutorialStorage = async () => {
    try {
      const storage = await AppStorage.create();
      const isOnboardingCleared = await storage.get('expired_date_for_onboarding');
      await storage.set('skip_onboarding', !!isOnboardingCleared);
      resetTooltip('appInstall');
    } catch (e) {
      //
    }
  };

  useEffect(() => {
    if (props.me === undefined) {
      setAuthState({
        type: 'signedOut',
        callaboApiClient: new CallaboApiClient({ BASE: CALLABO_API_BASE_URL }),
      });
      return;
    }

    if (authState.type === 'signedIn' && authState.user.id === props.me.id) {
      setAuthState({
        type: 'signedIn',
        callaboApiClient: authState.callaboApiClient,
        user: new UserModel(props.me),
        token: authState.token,
      });
    }

    (async () => {
      try {
        const accessToken = getCookie('access_token');
        const refreshToken = getCookie('refresh_token');
        const tokenExpiredDate = getCookie('token_expired_date');
        if (!accessToken || !refreshToken) {
          await onSignOut();
          return;
        }
        const callaboApiClient = new CallaboApiClient({
          BASE: CALLABO_API_BASE_URL,
        });
        if (!tokenExpiredDate || dateIsOver(new Date(tokenExpiredDate))) {
          const refreshResponse = await callaboApiClient.user.authV1UserAuthPost({
            grant_type: 'refresh_token',
            refresh_token: refreshToken,
          });
          await onSignIn(refreshResponse as LoginResponse);
          return;
        }

        const authTokenManager = await AuthTokenManager.createAsync(
          getOrCreateDeviceId(),
          async (response) => {
            try {
              await onSignIn(response);
            } catch (e) {
              onSignOut();
            }
          }
        );
        authTokenManager.event.on('unauthorized', onSignOut);
        authTokenManager.event.on('accessRestricted', () => {
          window.location.reload();
        });

        const user = new UserModel(props.me);

        setAuthState({
          type: 'signedIn',
          callaboApiClient: new CallaboApiClient({
            BASE: CALLABO_API_BASE_URL,
            TOKEN: accessToken,
            INSTANCE: authTokenManager.axiosInstance,
          }),
          user,
          token: {
            accessToken,
            refreshToken,
            expiredDate: new Date(tokenExpiredDate),
          },
        });

        const storage = await getIndexedDbStorageAsync();
        await storage.setMany([
          {
            key: 'access_token',
            value: accessToken,
          },
          {
            key: 'refresh_token',
            value: refreshToken,
          },
          {
            key: 'token_expired_date',
            value: tokenExpiredDate,
          },
        ]);
      } catch (error) {
        await onSignOut();
      }
    })();
  }, [props.me]);

  useEffect(() => {
    if (authState.type !== 'signedIn') return;
    const { user } = authState;
    analyticsInstance.setUserId(user.id?.toString());
  }, [authState.type]);

  useEffect(() => {
    (async () => {
      if (!pushToken) return;
      if (authState.type !== 'signedIn') return;
      const { callaboApiClient } = authState;
      const deviceId = getOrCreateDeviceId();
      await callaboApiClient.user.pushTokenV1UserPushTokenPost({
        device_id: deviceId,
        platform: PushDeviceRequest.platform.WEB,
        push_token: pushToken,
      });
    })();
  }, [pushToken, authState.type]);

  useEffect(() => {
    const hideUserPrivacy = PHASE === Phase.production;
    if (authState.type === 'signedIn') {
      const { user } = authState;
      setUserToSentry({
        email: hideUserPrivacy ? undefined : user.email,
        name: hideUserPrivacy ? undefined : user.name,
        username: hideUserPrivacy ? `${user.id}` : user.email,
        uid: `${user.id}`,
      });
    }
  }, [authState]);

  const signUpEmailAsync = useCallback(
    async (
      data: Body_signup_v1_user_post,
      token: {
        verify?: string;
        invitation?: string;
      }
    ): Promise<void> => {
      if (authState.type !== 'signedOut') {
        throw new Error('Invalid auth state: ' + `${authState.type}`, { cause: authState });
      }
      const { callaboApiClient } = authState;
      let loginResponse: LoginResponse;
      try {
        loginResponse = await callaboApiClient.user.signupV1UserPost({
          user: data.user,
          agreements: data.agreements,
          verify_token: token.verify || undefined,
          invitation_token: token.invitation || undefined,
        });
      } catch (e) {
        if (e instanceof ApiError) {
          switch (e.status) {
            case 401:
              throw new InvalidInvitationTokenError();
            case 403:
              throw new UnownedInvitationTokenError();
            case 409:
              throw new DuplicatedEmailError();
            case 429:
              throw new RateLimitedError();
          }
        }
        alert(`[${e.status}] ${e.message}`);
        await onSignOut();
        throw e;
      }
      await onSignIn(loginResponse as LoginResponse);
      await setTutorialStorage();
    },
    [authState]
  );

  const loginEmailAsync = useEventCallback(
    async (data: { email: string; password: string }): Promise<void> => {
      const deviceId = getOrCreateDeviceId();
      const trustedToken = getTrustedTokenByCurrentEmail(data.email);
      const actionResult = await loginWithEmailAction(
        data.email,
        data.password,
        deviceId,
        trustedToken
      );
      if (isActionError(actionResult)) {
        switch (actionResult.error.status) {
          case 400:
          case 422:
            throw new InvalidCredentialsError();
          case 429:
            throw new RateLimitedError();
        }
        alert(`[${actionResult.error.status}] ${actionResult.error.message}`);
        throw new Error(actionResult.error.message);
      }
      await setTutorialStorage();
    }
  );

  const loginPasswordSetupAsync = useEventCallback(async (password: string, token: string) => {
    const actionResult = await loginWithPasswordSetup(password, token);
    if (isActionError(actionResult)) {
      switch (actionResult.error.status) {
        case 401:
          throw new InvalidInvitationTokenError();
        case 429:
          throw new RateLimitedError();
      }
      alert(`[${actionResult.error.status}] ${actionResult.error.message}`);
      throw new Error(actionResult.error.message);
    }
  });

  const signUpFinishAsync = useEventCallback(
    async (agreements: Array<AgreementBase>, name: string, company: string): Promise<void> => {
      if (authState.type !== 'signedIn') return;
      const { callaboApiClient, user } = authState;
      try {
        if (user.userStatus !== UserStatus.CREATED) {
          await onSignOut();
          throw Error('Invalid user status');
        }
        await callaboApiClient.user.acceptTermV1UserAcceptancePatch(agreements);
        await callaboApiClient.user.modifyV1UserPatch({
          name: name,
          company: company,
        });
      } catch (e) {
        await onSignOut();
        alert(`[${e.status}] ${e.message}`);
        throw e;
      }
      await setTutorialStorage();
    }
  );

  const loginGoogleAsync = useEventCallback(
    async (invitationToken?: string, loginHint?: string): Promise<void> => {
      const provider = new GoogleAuthProvider();
      loginHint &&
        provider.setCustomParameters({
          login_hint: loginHint,
        });
      const auth = getAuth();
      let idToken: string;
      let email: string;
      try {
        await signInWithPopup(auth, provider);
        const currentUser = auth.currentUser;
        idToken = await currentUser.getIdToken();
        email = currentUser.email;
      } catch (e) {
        // firebase auth error인 경우 오류로 처리하지 않음
        if (FIREBASE_ERROR_CODE_EXCEPTIONS.includes(e.code)) return;
        analyticsInstance.logEvent('action_google_auth_fail', {
          code: e.code,
        });
        throw e;
      }
      const deviceId = getOrCreateDeviceId();
      const trustedToken = getTrustedTokenByCurrentEmail(email);
      const actionResult = await loginWithOAuthAction(
        {
          grant_type: 'google',
          id_token: idToken,
          invitation_token: invitationToken || undefined,
        },
        deviceId,
        trustedToken
      );
      if (isActionError(actionResult)) {
        switch (actionResult.error.status) {
          case 401:
            throw new InvalidInvitationTokenError();
          case 403:
            throw new UnownedInvitationTokenError();
          case 429:
            throw new RateLimitedError();
        }
        alert(`[${actionResult.error.status}] ${actionResult.error.message}`);
        throw new Error(actionResult.error.message);
      }
      await setTutorialStorage();
    }
  );

  const loginMicrosoftAsync = useEventCallback(
    async (invitationToken?: string, loginHint?: string): Promise<void> => {
      let accessToken = '';
      let email = '';
      try {
        const msalInstance = getMsalInstance();
        await msalInstance.initialize();
        msalInstance.clearCache();
        const loginResponse = await msalInstance.loginPopup({
          scopes: ['User.Read', 'email'],
          loginHint: loginHint,
        });

        if (
          loginResponse.idTokenClaims &&
          typeof loginResponse.idTokenClaims['email'] === 'string'
        ) {
          email = loginResponse.idTokenClaims['email'];
        }
        accessToken = loginResponse.accessToken;
      } catch (e) {
        // 유저가 종료한 경우 등은 오류로 보지 않음
        if (MSAL_ERROR_CODE_EXCEPTIONS.includes(e.errorCode)) return;
        analyticsInstance.logEvent('action_microsoft_auth_fail', {
          code: e.code,
        });
        throw e;
      }

      const deviceId = getOrCreateDeviceId();
      const trustedToken = getTrustedTokenByCurrentEmail(email);
      const actionResult = await loginWithOAuthAction(
        {
          grant_type: 'microsoft',
          access_token: accessToken,
          invitation_token: invitationToken || undefined,
        },
        deviceId,
        trustedToken
      );

      if (isActionError(actionResult)) {
        switch (actionResult.error.status) {
          case 401:
            throw new InvalidInvitationTokenError();
          case 403:
            throw new UnownedInvitationTokenError();
          case 429:
            throw new RateLimitedError();
        }
        alert(`[${actionResult.error.status}] ${actionResult.error.message}`);
        throw new Error(actionResult.error.message);
      }
      await setTutorialStorage();
    }
  );

  // const loginAppleAsync = useEventCallback(
  //   async (invitationToken?: string, loginHint?: string): Promise<void> => {
  //     const provider = new OAuthProvider('apple.com');
  //     loginHint &&
  //       provider.setCustomParameters({
  //         login_hint: loginHint,
  //       });
  //     const auth = getAuth();
  //     let idToken: string;
  //     try {
  //       await signInWithPopup(auth, provider);
  //       const currentUser = auth.currentUser;
  //       idToken = await currentUser.getIdToken();
  //     } catch (e) {
  //       // user가 인증을 중단한 경우 오류로 처리하지 않음
  //       if (AUTH_ERROR_CODE_EXCEPTIONS.includes(e.code)) return;
  //       analyticsInstance.logEvent('action_apple_auth_fail', {
  //         code: e.code,
  //       });
  //       throw e;
  //     }

  //     const actionResult = await loginWithOAuthAction({
  //       grant_type: AppleAuthentication.grant_type.APPLE,
  //       id_token: idToken,
  //       invitation_token: invitationToken || undefined,
  //     });
  //     if (isActionError(actionResult)) {
  //       switch (actionResult.error.status) {
  //         case 401:
  //           throw new InvalidInvitationTokenError();
  //         case 403:
  //           throw new UnownedInvitationTokenError();
  //         case 429:
  //           throw new RateLimitedError();
  //       }
  //       alert(`[${actionResult.error.status}] ${actionResult.error.message}`);
  //       throw new Error(actionResult.error.message);
  //     }
  //     await setTutorialStorage();
  //   }
  // );

  const modifyUserAsync = useCallback(
    async (data: UserUpdateRequest) => {
      if (authState.type !== 'signedIn') return;
      const actionResult = await modifyUserAction(data);
      if (isActionError(actionResult)) {
        throw new Error();
      }
      setAuthState({
        ...authState,
        user: new UserModel(actionResult.result),
      });
    },
    [authState]
  );

  const logoutAsync = useCallback(async (): Promise<any> => {
    const deviceId = getOrCreateDeviceId();
    await logoutAction({ device_id: deviceId, push_token: pushToken });
    await onSignOut(new PathName(Paths.Index).toString());
  }, [authState, pushToken]);

  const withdrawAsync = useCallback(async (): Promise<void> => {
    if (authState.type !== 'signedIn') return;
    const { callaboApiClient } = authState;
    await callaboApiClient.user.withdrawV1UserDelete();
    await onSignOut(new PathName(Paths.Index).toString());
  }, [authState]);

  return (
    <AuthContext.Provider
      value={{
        authState,
        signUpFinishAsync,
        signUpEmailAsync,
        loginGoogleAsync,
        loginMicrosoftAsync,
        // loginAppleAsync,
        loginEmailAsync,
        loginPasswordSetupAsync,
        logoutAsync,
        modifyUserAsync,
        withdrawAsync,
        getAuthState,
      }}
    >
      <ApiClientContext.Provider
        value={
          authState.type === 'loading'
            ? new CallaboApiClient({ BASE: CALLABO_API_BASE_URL })
            : authState.callaboApiClient
        }
      >
        {props.children}
      </ApiClientContext.Provider>
    </AuthContext.Provider>
  );
};
